import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Droplets, Sprout, Leaf, ArrowUpRight, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { KineticHeadline, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SeoHead } from "@/components/SeoHead";
import { staggerContainer, fadeUp } from "@/lib/motion";
import { useContent } from "@/context/ContentContext";

const ICONS = [Droplets, Sprout, Leaf];

export default function Home() {
  const { t } = useTranslation();
  const { media } = useContent();
  const heroLines = t("home.heroLines", { returnObjects: true });
  const cards = t("home.cards", { returnObjects: true });

  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const [aspect, setAspect] = useState(4 / 5); // fallback until metadata loads
  const [muted, setMuted] = useState(true);

  const handleMeta = (e) => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight) {
      setAspect(v.videoWidth / v.videoHeight);
    }
  };

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // Attempt play in case autoplay was blocked when unmuted
    if (!v.muted) {
      v.play().catch(() => {});
    }
  };

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const mediaY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <div data-testid="home-page">
      <SeoHead pageKey="home" />
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 min-h-[88vh] items-center py-16">
          {/* Left */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-6"
            >
              {t("home.overline")}
            </motion.p>

            <KineticHeadline
              lines={heroLines}
              testId="home-hero-title"
              className="font-display font-bold text-[#1a3636] text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.02] tracking-tighter"
            />

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-8 text-base md:text-lg text-[#1a3636]/65 leading-relaxed max-w-xl"
            >
              {t("home.heroSub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/cozumler"
                data-testid="hero-btn-corporate"
                className="group inline-flex items-center justify-center gap-2 bg-[#1a3636] text-[#f8faf9] px-8 py-4 rounded-full font-medium hover:bg-[#2b8a7e] transition-colors duration-300"
              >
                {t("home.btnCorporate")}
                <ArrowRight size={18} strokeWidth={1.8} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/donanim"
                data-testid="hero-btn-individual"
                className="inline-flex items-center justify-center gap-2 border border-[#1a3636]/25 text-[#1a3636] px-8 py-4 rounded-full font-medium hover:bg-[#1a3636] hover:text-[#f8faf9] transition-all duration-300"
              >
                {t("home.btnIndividual")}
              </Link>
            </motion.div>
          </div>

          {/* Right - parallax media placeholder */}
          <motion.div
            style={{ y: mediaY }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            {/* ==== MACHINE WORKING VIDEO ==== */}
            <div
              data-testid="home-hero-media"
              style={{ aspectRatio: aspect, maxHeight: "80vh" }}
              className="group w-full rounded-xl shadow-lg border-4 border-white overflow-hidden relative bg-[#1a3636]"
            >
              <video
                ref={videoRef}
                src={media.heroVideo}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedMetadata={handleMeta}
                className="block w-full h-full"
                data-testid="home-hero-video"
              />

              {/* Sound toggle — visible on hover / focus */}
              <button
                type="button"
                onClick={toggleSound}
                data-testid="home-hero-sound-toggle"
                aria-label={muted ? "Sesi aç" : "Sesi kapat"}
                className="absolute bottom-4 right-4 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-[#1a3636]/70 text-[#f8faf9] backdrop-blur border border-white/20 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 focus:opacity-100 focus:translate-y-0 hover:bg-[#2b8a7e] transition-all duration-300"
              >
                {muted ? (
                  <VolumeX size={18} strokeWidth={1.7} />
                ) : (
                  <Volume2 size={18} strokeWidth={1.7} />
                )}
              </button>

              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#2b8a7e]/20 blur-2xl pointer-events-none" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white rounded-2xl px-5 py-4 shadow-[0_20px_40px_rgb(26,54,54,0.12)] border border-[#1a3636]/5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#2b8a7e] animate-pulse" />
              <span className="text-sm font-medium text-[#1a3636]">ESP32 · Live Monitoring</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE DIVIDER */}
      <div className="border-y border-[#1a3636]/8 text-[#1a3636]/25">
        <Marquee items={["Hydroflow", "Wate Logic", "ESP32", "Phyto-Colostrum", "%95 Tasarruf", "Bio Systems"]} />
      </div>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-4">
              {t("home.howSub")}
            </p>
            <h2 className="font-display font-bold text-[#1a3636] text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-2xl">
              {t("home.howTitle")}
            </h2>
          </Reveal>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
          >
            {cards.map((card, i) => {
              const Icon = ICONS[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`how-card-${i}`}
                  className="group bg-white rounded-2xl p-8 border border-[#1a3636]/5 shadow-[0_8px_30px_rgb(26,54,54,0.06)] hover:shadow-[0_20px_40px_rgb(43,138,126,0.12)] hover:-translate-y-1 transition-all duration-500"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-14 h-14 rounded-2xl bg-[#2b8a7e]/10 flex items-center justify-center text-[#2b8a7e] group-hover:bg-[#2b8a7e] group-hover:text-white transition-colors duration-500">
                      <Icon size={26} strokeWidth={1.5} />
                    </div>
                    <span className="font-display text-5xl font-light text-[#1a3636]/10">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1a3636] tracking-tight mb-3">
                    {card.title}
                  </h3>
                  <p className="text-[#1a3636]/60 leading-relaxed">{card.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="relative overflow-hidden rounded-[2rem] bg-[#1a3636] px-8 md:px-16 py-16 md:py-20">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#2b8a7e]/25 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <h2 className="font-display font-bold text-[#f8faf9] text-3xl md:text-4xl tracking-tight max-w-2xl">
                {t("tech.title")}
              </h2>
              <Link
                to="/teknoloji"
                data-testid="home-cta-tech"
                className="group inline-flex items-center gap-2 bg-[#f8faf9] text-[#1a3636] px-8 py-4 rounded-full font-medium hover:bg-[#2b8a7e] hover:text-white transition-colors duration-300 whitespace-nowrap"
              >
                {t("nav.technology")}
                <ArrowUpRight size={18} strokeWidth={1.8} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
